// src/App.js
// src/App.js
import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "./config";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { useParams, useNavigate } from "react-router-dom";

import JobList from "./components/JobList";
import ChatbotInterface from "./components/ChatbotInterface";
import HRDashboard from "./components/hr/HRDashboard";
import HRMain from "./components/hr/HRMain";
import ViewJobs from "./components/hr/ViewJobs";
import PostJobForm from "./components/hr/PostJobForm";
import TryMe from "./components/hr/TryMe"; // Import TryMe component here
import HRJobApplications from "./components/hr/HRJobApplications";
import ApplyModal from "./components/ApplyModal";
import SubmissionSuccessModal from "./components/SubmissionSuccessModal";
import CalendarPage from "./components/CalendarPage";
import Login from "./components/auth/Login";
import ProtectedRoute from "./auth/ProtectedRoute";

import "./App.css";
// const API_BASE_URL = "https://screening.fanisko.com";
// const API_BASE_URL = "http://127.0.0.1:8000";

function ApplyModalWrapper({ jobs, onStartChatbot }) {
  const { jobSlug } = useParams();
  const navigate = useNavigate();

  // Extract jobid from jobSlug (jobname-jobid format)
  const lastHyphenIndex = jobSlug.lastIndexOf("-");
  const jobId = jobSlug.substring(lastHyphenIndex + 1);

  const job = jobs.find((j) => j.jobid === jobId);

  // Local state to handle submission success modal and candidate info
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [candidateName, setCandidateName] = useState("");
  const [candidateId, setCandidateId] = useState("");
  const [highScore, setHighScore] = useState(false);

  if (!job) return <div>Job not found</div>;

  // Handler when ApplyModal signals successful submission
  const handleSuccess = (name, job, score, candidateId) => {
    setCandidateName(name);
    setCandidateId(candidateId);
    setHighScore(score >= 40);
    setShowSuccessModal(true);
  };

  // Close success modal and navigate to candidate portal root "/"
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate("/"); // or keep user here if you want
  };

  // Continue to interview chat - call onStartChatbot if passed, then close modal and navigate
  const handleContinue = () => {
    if (onStartChatbot && job) {
      onStartChatbot(candidateName, job, candidateId);
    }
    setShowSuccessModal(false);
    navigate("/"); // or wherever chat UI is shown
  };

  return (
    <>
      {!showSuccessModal && (
        <ApplyModal job={job} onClose={() => navigate("/")} onSuccess={handleSuccess} />
      )}

      {showSuccessModal && (
        <SubmissionSuccessModal
          candidateName={candidateName}
          job={job}
          highScore={highScore}
          onClose={handleCloseSuccessModal}
          onContinue={handleContinue}
        />
      )}
    </>
  );
}

function App() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch jobs from backend
  useEffect(() => {
    async function fetchJobs() {
      setLoading(true);
      try {
        // const res = await fetch("https://screening.fanisko.com/api/jobviewmain");
        const res = await fetch(`${API_BASE_URL}/api/jobviewmain`);
        if (!res.ok) throw new Error("Failed to fetch jobs");
        const data = await res.json();
        setJobs(data); // Your backend should return an array of jobs
      } catch (error) {
        setJobs([]);
      }
      setLoading(false);
    }
    fetchJobs();
  }, []);

  const [showChatbot, setShowChatbot] = useState(false);
  const [candidateName, setCandidateName] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [candidateId, setCandidateId] = useState("");

  // States for submission success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [screeningScore, setScreeningScore] = useState(0);
  const [highScore, setHighScore] = useState(false);

  // Called when user begins chatbot independently (optional)
  const handleStartChatbot = (name, job, candidateId) => {
    setCandidateId(candidateId);
    setCandidateName(name);
    setSelectedJob(job);
    setShowChatbot(true);
  };

  // Called after successful application submission with screening score
  const handleSubmissionSuccess = (name, job, score, candidateId) => {
    setCandidateId(candidateId);
    setCandidateName(name);
    setSelectedJob(job);
    setScreeningScore(score);
    setHighScore(score >= 40);
    setShowSuccessModal(true);
    setShowChatbot(false);
  };

  // Interview chat open handler
  const openInterviewChat = () => {
    setShowChatbot(true);
  };

  const addJob = (job) => {
    setJobs((prevJobs) => [job, ...prevJobs]);
  };

  if (loading) {
    return (
      <div style={{ padding: 60, color: "#293a70", fontWeight: 700 }}>
        Loading jobs...
      </div>
    );
  }

  const editJob = (updatedJob) => {
    setJobs((jobs) =>
      jobs.map((job) => (job.jobid === updatedJob.jobid ? updatedJob : job))
    );
  };

  const toggleJobVisibility = (jobId) => {
    setJobs((jobs) =>
      jobs.map((job) =>
        job.jobid === jobId ? { ...job, visible: !job.visible } : job
      )
    );
  };

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/calendar" element={<CalendarPage />} />
        {/* Candidate portal route */}
        <Route
          path="/"
          element={
            <div className="app-bg">
              {!showChatbot ? (
                <>
                  <header className="portal-header">
                    <div className="portal-header-row">
                      <div>
                        <div className="portal-title">Fanisko Careers</div>
                        <div className="portal-subtitle">Find your next opportunity</div>
                      </div>
                      <div className="portal-actions">
                        <input className="search-box" placeholder="Search" />
                      </div>
                    </div>
                  </header>
                  <main className="main-content">
                    <JobList
                      jobs={jobs}
                      onStartChatbot={(name, job, candidateId) => {
                        setCandidateId(candidateId);
                        setCandidateName(name);
                        setSelectedJob(job);
                        setShowChatbot(true);
                      }}
                    />

                    {/* Application form/modal should be rendered here and passed onSuccess={handleSubmissionSuccess} */}

                    <>
                      {console.log("App Render - showSuccessModal:", showSuccessModal, "highScore:", highScore)}
                      {showSuccessModal && (
                        <SubmissionSuccessModal
                          candidateName={candidateName}
                          job={selectedJob}
                          highScore={highScore}
                          onClose={() => setShowSuccessModal(false)}
                          onContinue={() => {
                            setShowSuccessModal(false);
                            openInterviewChat();
                          }}
                        />
                      )}
                    </>
                  </main>
                </>
              ) : (
                <ChatbotInterface userName={candidateName} job={selectedJob} candidateId={candidateId} />
              )}
            </div>
          }
        />

        {/* Apply modal wrapper route */}
        {/* Public apply route */}
        <Route
          path="/apply/:jobSlug"
          element={
            <ApplyModalWrapper
              jobs={jobs}
              onStartChatbot={handleStartChatbot}
            />
          }
        />

        {/* PROTECTED HR ROUTES */}
        <Route element={<ProtectedRoute />}>
          <Route
            path="/hr/*"
            element={
              <HRMain
                jobs={jobs}
                addJob={addJob}
                editJob={editJob}
                toggleJobVisibility={toggleJobVisibility}
              />
            }
          >
            <Route index element={<HRDashboard jobs={jobs} />} />
            <Route
              path="viewjobs"
              element={
                <ViewJobs
                  jobs={jobs}
                  onEditJob={editJob}
                  onToggleVisibility={toggleJobVisibility}
                />
              }
            />
            <Route path="postjob" element={<PostJobForm addJob={addJob} />} />
            <Route path="uploadresume" element={<TryMe jobs={jobs} />} />
          </Route>

          {/* Example: protect detailed HR applications too */}
          <Route
            path="/hr/jobs/:jobrole/:location/:jobid"
            element={<HRJobApplications />}
          />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
